import React, { useEffect } from 'react';
import { useAccessibility } from '../context/AccessibilityContext';
import { useLanguage } from '../context/LanguageContext';

const AccessibilityPanel: React.FC = () => {
  const { 
    settings, 
    updateSetting, 
    resetSettings, 
    isAccessibilityPanelOpen, 
    toggleAccessibilityPanel,
    speakText,
    stopSpeaking,
    isSpeaking,
    currentSpeakingElement
  } = useAccessibility();
  const { t } = useLanguage();

  // Handle Escape key to close panel
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isAccessibilityPanelOpen) {
        toggleAccessibilityPanel();
      }
    };

    if (isAccessibilityPanelOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isAccessibilityPanelOpen, toggleAccessibilityPanel]);

  if (!isAccessibilityPanelOpen) return null;

  const handleSettingChange = (key: keyof typeof settings, value: any) => {
    updateSetting(key, value);
    // Anunță schimbarea pentru utilizatorii cu screen reader-e
    if (settings.textToSpeech) {
      speakText(t(`accessibility.${String(key)}Changed`));
    }
  };

  const handleTestSpeech = () => {
    if (isSpeaking) {
      stopSpeaking();
    } else {
      speakText(t('accessibility.testSpeech'));
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
        onClick={(e) => {
          // Închide panelul doar dacă se face click pe overlay, nu pe panel
          if (e.target === e.currentTarget) {
            toggleAccessibilityPanel();
          }
        }}
        aria-hidden="true"
      />
      
      {/* Panel */}
      <div 
        className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-[9998] overflow-y-auto accessibility-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="accessibility-panel-title"
      >
        {/* Header */}
        <div className="sticky top-0 bg-blue-600 text-white p-4 z-10">
          <div className="flex items-center justify-between">
            <h2 id="accessibility-panel-title" className="text-xl font-bold">
              {t('accessibility.title')}
            </h2>
            <button
              onClick={toggleAccessibilityPanel}
              className="p-2 hover:bg-blue-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={t('accessibility.closePanel')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-blue-100 text-sm mt-2">
            {t('accessibility.description')}
          </p>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Mărimea textului */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              {t('accessibility.fontSize')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['small', 'medium', 'large', 'extra-large'] as const).map(size => (
                <button
                  key={size}
                  onClick={() => handleSettingChange('fontSize', size)}
                  className={`
                    px-3 py-2 text-sm rounded-lg border transition-all
                    ${settings.fontSize === size 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }
                  `}
                  aria-pressed={settings.fontSize === size}
                >
                  {t(`accessibility.fontSize.${size}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Contrast */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              {t('accessibility.contrast')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['normal', 'high'] as const).map(contrast => (
                <button
                  key={contrast}
                  onClick={() => handleSettingChange('contrast', contrast)}
                  className={`
                    px-3 py-2 text-sm rounded-lg border transition-all
                    ${settings.contrast === contrast 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }
                  `}
                  aria-pressed={settings.contrast === contrast}
                >
                  {t(`accessibility.contrast.${contrast}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Text-to-Speech */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.textToSpeech')}
              </label>
              <button
                onClick={() => handleSettingChange('textToSpeech', !settings.textToSpeech)}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.textToSpeech ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.textToSpeech}
                aria-label={t('accessibility.toggleTextToSpeech')}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.textToSpeech ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>
            
            {settings.textToSpeech && (
              <div className="space-y-2">
                <button
                  onClick={handleTestSpeech}
                  className={`
                    w-full px-3 py-2 text-sm rounded-lg border transition-all
                    ${isSpeaking 
                      ? 'bg-red-600 text-white border-red-600 hover:bg-red-700' 
                      : 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                    }
                  `}
                >
                  {isSpeaking ? t('accessibility.stopSpeech') : t('accessibility.testSpeech')}
                </button>
                
                {currentSpeakingElement && (
                  <div className="text-xs text-gray-600 p-2 bg-gray-100 rounded">
                    <span className="font-medium">{t('accessibility.speaking')}: </span>
                    {currentSpeakingElement}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Alte opțiuni de accesibilitate */}
          <div className="space-y-4">
            {/* Reduced Motion */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.reducedMotion')}
              </label>
              <button
                onClick={() => handleSettingChange('reducedMotion', !settings.reducedMotion)}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.reducedMotion ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.reducedMotion}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.reducedMotion ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>

            {/* Focus Indicator */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.focusIndicator')}
              </label>
              <button
                onClick={() => handleSettingChange('focusIndicator', !settings.focusIndicator)}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.focusIndicator ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.focusIndicator}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.focusIndicator ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>

            {/* Dyslexia Font */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.dyslexiaFont')}
              </label>
              <button
                onClick={() => handleSettingChange('dyslexiaFont', !settings.dyslexiaFont)}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.dyslexiaFont ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.dyslexiaFont}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.dyslexiaFont ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>

            {/* Line Spacing */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.lineSpacing')}
              </label>
              <button
                onClick={() => handleSettingChange('lineSpacing', settings.lineSpacing === 'normal' ? 'increased' : 'normal')}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.lineSpacing === 'increased' ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.lineSpacing === 'increased'}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.lineSpacing === 'increased' ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>

            {/* Underline Links */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t('accessibility.underlineLinks')}
              </label>
              <button
                onClick={() => handleSettingChange('underlineLinks', !settings.underlineLinks)}
                className={`
                  relative w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
                  ${settings.underlineLinks ? 'bg-blue-600' : 'bg-gray-300'}
                `}
                role="switch"
                aria-checked={settings.underlineLinks}
              >
                <div className={`
                  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform
                  ${settings.underlineLinks ? 'translate-x-6' : 'translate-x-0'}
                `} />
              </button>
            </div>
          </div>

          {/* Reset button */}
          <div className="pt-4 border-t">
            <button
              onClick={() => {
                resetSettings();
                if (settings.textToSpeech) {
                  speakText(t('accessibility.settingsReset'));
                }
              }}
              className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('accessibility.resetSettings')}
            </button>
          </div>

          {/* Keyboard shortcuts info */}
          <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            <h4 className="font-semibold mb-2">{t('accessibility.keyboardShortcuts')}</h4>
            <ul className="space-y-1">
              <li><kbd className="px-2 py-1 bg-gray-200 rounded">Alt + A</kbd> - {t('accessibility.openPanelShortcut')}</li>
              <li><kbd className="px-2 py-1 bg-gray-200 rounded">Alt + S</kbd> - {t('accessibility.toggleSpeechShortcut')}</li>
              <li><kbd className="px-2 py-1 bg-gray-200 rounded">Esc</kbd> - {t('accessibility.closePanelShortcut')}</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default AccessibilityPanel; 